import { useState, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Loader2, ZoomIn, ZoomOut, ExternalLink, AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import * as Sentry from '@sentry/react'
import type { BoundingBox } from '@/types/api'
import { apiFetch } from '@/lib/apiFetch'

// Configure worker (safe to set multiple times — it's idempotent)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

interface PdfViewerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentId: string | undefined
  /** Aligned bounding boxes to render as yellow highlights, across one or
   *  more pages. Pass an empty list to render the PDF without highlights. */
  boxes: BoundingBox[]
}

export function PdfViewerModal({
  open,
  onOpenChange,
  documentId,
  boxes,
}: PdfViewerModalProps) {
  const [scale, setScale] = useState(1.0)
  const [numPages, setNumPages] = useState(0)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  // Track per-page render failures. pdf.js silently fails to render certain
  // image streams found in older scanned PDFs (CCITT G4 fax, JBIG2 variants).
  // We capture the error and offer the browser's native viewer as fallback.
  const [pageRenderErrors, setPageRenderErrors] = useState<Map<number, string>>(new Map())

  // Fetch a fresh signed URL each time the modal opens
  useEffect(() => {
    if (!open) {
      /* eslint-disable react-hooks/set-state-in-effect -- reset cached URL when modal closes; next open re-fetches */
      setPdfUrl(null)
      setLoadError(null)
      setPageRenderErrors(new Map())
      /* eslint-enable react-hooks/set-state-in-effect */
      return
    }
    if (!documentId) {
      setLoadError('No source document is linked to this claim.')
      return
    }
    setLoadError(null)
    setPdfUrl(null)
    apiFetch(`/api/sources/${documentId}/pdf-url`)
      .then((r) => {
        if (!r.ok) throw new Error(`Could not fetch PDF URL (HTTP ${r.status})`)
        return r.json() as Promise<{ url: string }>
      })
      .then(({ url }) => setPdfUrl(url))
      .catch((err: unknown) => {
        // Unexpected: a backend or infra failure (signed-URL 5xx, network /
        // CORS error). Report it — the frontend is the only place that can
        // see browser→GCS failures, which never reach the backend's Sentry.
        Sentry.captureException(err, { extra: { feature: 'pdf-viewer', sourceId: documentId } })
        setLoadError(
          `Failed to load PDF: ${err instanceof Error ? err.message : String(err)}`,
        )
      })
  }, [open, documentId])

  // Collect all unique pages that have bounding boxes, sorted ascending.
  // When there are no boxes, render every page so the user can browse the
  // full document (Library "Open PDF" affordance). numPages is 0 until the
  // PDF finishes loading, hence the [1] fallback to render the first page
  // while we wait.
  const boxPages = [...new Set(boxes.map((b) => b.page_number))].sort((a, b) => a - b)
  const allPages = numPages > 0 ? Array.from({ length: numPages }, (_, i) => i + 1) : [1]
  const pageNumbers = boxPages.length > 0 ? boxPages : allPages
  // Group boxes by page
  const boxesByPage = new Map<number, BoundingBox[]>()
  for (const b of boxes) {
    const arr = boxesByPage.get(b.page_number) ?? []
    arr.push(b)
    boxesByPage.set(b.page_number, arr)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="!max-w-[95vw] !w-[95vw] !h-[90vh] flex flex-col p-0 gap-0"
      >
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-sm font-semibold">
              Reference PDF
              {boxes.length > 0
                ? <> — Page{pageNumbers.length > 1 ? 's' : ''} {pageNumbers.join(', ')}{numPages > 0 && ` of ${numPages}`}</>
                : numPages > 0 && <> — {numPages} page{numPages > 1 ? 's' : ''}</>}
            </DialogTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground w-12 text-center tabular-nums">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              {pdfUrl && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Open in browser viewer"
                  onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-auto bg-[#888] flex justify-center p-6 min-h-0">
          {loadError ? (
            <div className="self-center text-white text-sm bg-red-700/60 px-4 py-3 rounded-lg max-w-sm text-center">
              {loadError}
            </div>
          ) : !pdfUrl ? (
            <div className="self-center flex items-center gap-2 text-white/70 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading PDF…
            </div>
          ) : (
            <div className="flex flex-col items-stretch gap-3 w-full">
              {pageRenderErrors.size > 0 && (
                <div className="self-center max-w-2xl flex items-start gap-2 text-xs bg-amber-100 text-amber-900 border border-amber-300 px-3 py-2 rounded-md">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium">
                      Some pages couldn&apos;t be rendered here ({pageRenderErrors.size}{numPages > 0 ? ` of ${numPages}` : ''}).
                    </div>
                    <div className="text-amber-800 mt-0.5">
                      This often happens with older scanned PDFs whose image
                      compression isn&apos;t supported by the in-page viewer.
                      Open it in your browser&apos;s native PDF viewer instead:
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-amber-900 underline px-0 h-auto mt-1"
                      onClick={() => window.open(pdfUrl!, '_blank', 'noopener,noreferrer')}
                    >
                      Open PDF in new tab →
                    </Button>
                  </div>
                </div>
              )}
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                onLoadError={(err) => {
                  // The signed URL resolved but pdf.js couldn't fetch/parse the
                  // file (GCS CORS, corrupt or truncated PDF). Worth reporting.
                  Sentry.captureException(err, { extra: { feature: 'pdf-viewer', sourceId: documentId } })
                  setLoadError(`Failed to load PDF: ${err.message}`)
                }}
              >
                {/* Render all pages that have bounding boxes, stacked vertically */}
                <div className="flex flex-col items-center gap-4">
                  {pageNumbers.map((pg) => (
                    <div key={pg} style={{ position: 'relative', display: 'inline-block' }}>
                      <Page
                        pageNumber={pg}
                        scale={scale}
                        renderTextLayer
                        renderAnnotationLayer
                        onRenderError={(err) => {
                          // pdf.js silently fails on some image streams in
                          // legacy scanned PDFs — capture so the user can
                          // fall back to the native browser viewer.
                          console.warn(`[PdfViewer] page ${pg} render error:`, err)
                          setPageRenderErrors((prev) => {
                            const next = new Map(prev)
                            next.set(pg, err instanceof Error ? err.message : String(err))
                            return next
                          })
                        }}
                      />

                      {/* Yellow highlight overlays — bbox coords are in inches; ×72 converts to PDF points */}
                      {(boxesByPage.get(pg) ?? []).map(({ bbox }, i) => (
                        <div
                          key={i}
                          style={{
                            position: 'absolute',
                            left: bbox.min_x * 72 * scale,
                            top: bbox.min_y * 72 * scale,
                            width: bbox.width * 72 * scale,
                            height: bbox.height * 72 * scale,
                            backgroundColor: 'rgba(255, 255, 0, 0.4)',
                            mixBlendMode: 'multiply',
                            pointerEvents: 'none',
                            border: '1px solid rgba(200, 160, 0, 0.6)',
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </Document>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
